import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { catchError, combineLatest, delay, forkJoin, map, of, startWith } from 'rxjs';
import { OBPPLoginService } from 'src/app/services/login-page/login-page.service';
import { AdminApproveRegistrationService } from 'src/app/services/admin-approve-registration/admin-approve-registration.services';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalRejectUserComponent } from 'src/app/common/shared/pop-up-modal/modal-reject-user/modal-reject-user.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TRIMMED_EMAIL_REGEX } from 'src/app/common/email-validator';
import { ModalAccountDecisionResultComponent } from 'src/app/common/shared/pop-up-modal/modal-account-decision-result/modal-account-decision-result.component';
import { ModalPendingUserComponent } from 'src/app/common/shared/pop-up-modal/modal-pending-user/modal-pending-user.component';

@Component({
  selector: 'app-obpp-approve-registration-account',
  templateUrl: './obpp-approve-registration-account.component.html',
  styleUrls: ['./obpp-approve-registration-account.component.css']
})
export class ObppApproveRegistrationAccountComponent implements OnInit {
  email: boolean = true;
  accountNumber: boolean = false;
  requestedAccountNumber: String = '';
  requestedProcessedBy: String = '';
  requestedProcessedDecision: String = '';
  requestedProcessedReason: String = '';
  requestedProcessedDate: String = '';
  userSearchCondition: boolean = false;
  searchForm: FormGroup;
  choice: string = 'email';
  selectData: any = new Map();
  showingUserList: any;
  allUsersList: any;
  p: number = 1;
  itemsPerPage: number = 25;
  totalItems: number = 0;
  totalPages: number = 0;
  isFirst: boolean = true;
  isLast: boolean = false;
  pages: (number | '...')[] = [];
  flattenedAccountList: any = [];
  loading: boolean = false;
  isRefreshing: boolean = false;
  searchUser = {
    emailSearch: '',
    phoneNumberSearch: '',
    accountNumberSearch: '',
  };
  showRejectModal = false;
  rejectContext: {
    row: any | null,
    acc: any | null
  } = { row: null, acc: null };

  constructor(
    private fb: FormBuilder,
    private obbpLoginService: OBPPLoginService,
    private adminApproveRegistrationService: AdminApproveRegistrationService,
    private location: Location,
    private modalService: NgbModal
  ) {

    this.searchForm = this.fb.group({
      emailSearch: [''],
      accountNumberSearch: ['']
    });

  }

  ngOnInit(): void {
    this.loadRegistrationData();
  }

  private loadRegistrationData(page: number = 1): void {
    this.loading = !this.isRefreshing;
    const params = this.buildSearchParams();
    const searchType = params.searchType;
    const searchValue = params.searchValue;

    this.adminApproveRegistrationService.getRegistrationAccountAllData(
      this.obbpLoginService.x_csrf_token,
      page,
      this.itemsPerPage,
      searchType,
      searchValue
    ).pipe(
        catchError(err => {
          console.error('Error fetching registration data:', err);
          this.loading = false;
          this.isRefreshing = false;
          return of(null);
        })
      )
      .subscribe(data => {
        this.loading = false;
        this.isRefreshing = false;

        const response = data?.serviceResponse?.object || {};
        if (data?.serviceResponse?.type === 'success' && response) {
          this.p = response.page;
          this.itemsPerPage = response.size;
          this.totalItems = response.totalElements;
          this.totalPages = response.totalPages;
          this.isFirst = response.first;
          this.isLast = response.last;
          this.allUsersList = response.pendingRegistrationResponseDtos || [];
          this.showingUserList = this.allUsersList;
          this.rebuildFlattenedAccounts();
          this.buildPages();
        }

        if (this.allUsersList.length === 0 && this.p > 1) {
          this.loadRegistrationData(this.p - 1);
        }
      });
  }

  private buildSearchParams(): { searchType: string, searchValue: string} {
    const emailValue = (this.searchForm.get('emailSearch')?.value || '').trim();
    const accountValue = (this.searchForm.get('accountNumberSearch')?.value || '').trim();

    if (this.choice === 'email' && emailValue) {
      return { searchType: 'emailAddress', searchValue: emailValue };
    } else if (this.choice === 'account' && accountValue) {
      return { searchType: 'accountNumber', searchValue: accountValue };
    }
    return { searchType: '', searchValue: '' };
  }

  private rebuildFlattenedAccounts(): void {
    this.flattenedAccountList = (this.showingUserList || []).map((user: any) => {
      const acc = user.pendingAccount;
      if (acc.decision === undefined) {
        acc.decision = null;
        acc.isSubmitting = false;
        acc.submitDone = false;
        acc.submitError = false;
      }
      return { user, acc };
    });
  }

  searchFormChange(choice: string) {
    if (choice === 'email') {
      this.email = true;
      this.accountNumber = false;
    } else if (choice === 'account') {
      this.email = false;
      this.accountNumber = true;
    }
    this.resetSearch();
    this.choice = choice;
  }

  searchFunc() {
    this.userSearchCondition = this.hasSearchCondition;
    this.p = 1;
    this.loadRegistrationData(1);
  }

  resetSearch() {
    this.searchForm.reset();
    this.userSearchCondition = false;
    this.p = 1;
    this.loadRegistrationData(1);
  }

  get hasSearchCondition(): boolean {
    const { emailSearch, accountNumberSearch } = this.searchForm.value;
    return !!(emailSearch?.trim() || accountNumberSearch?.trim());
  }

  onDecisionChange(user: any, acc: any) {
    acc.submitDone = false;
    acc.submitError = false;
    if (acc.decision !== 'R') {
      acc.rejectionReason = '';
    } 
    if (acc.decision === 'P') {
      acc.pendingNote = '';
    }
  }

  submitDecision(row: any, acc: any) {
    if (!acc.decision)  return;
    if (acc.decision === 'R' && !acc.rejectionReason) {
      this.openRejectModal(row, acc);
      return;
    }
    if (acc.decision === 'P' && !acc.pendingNote) {
      this.openPendingModal(row, acc);
      return;
    }

    acc.isSubmitting = true;
    const decisionPayload = {
      userID: row.user.userID,
      accountNumber: acc.accountNumber,
      OBPPPendingUserAccountID: acc.obpppendingUserAccountID,
      decision: acc.decision,
      createBy: acc.createBy,
      processBy: this.obbpLoginService.getUserName(),
      reason: acc.decision === 'R' ? acc.rejectionReason : undefined,
      notes: acc.decision === 'P' ? acc.pendingNote : undefined,
      privilege: acc.privilege
    };

    this.adminApproveRegistrationService.setRegistrationAccountDecision(
      decisionPayload,
      this.obbpLoginService.x_csrf_token)
    .pipe(
      catchError(err => {
        acc.submitError = true;
        acc.isSubmitting = false;
        return of(null);
      })
    )
    .subscribe(res => {
      acc.isSubmitting = false;
      if (!acc.submitError) acc.submitDone = true;

      const response = res?.serviceResponse?.object || null;
      if (res?.serviceResponse?.type === 'success' && response) {
        this.requestedAccountNumber = response.accountNumber;
        this.requestedProcessedBy = response.processBy;
        this.requestedProcessedDecision = response.status;
        this.requestedProcessedReason = response.reason || '';
        this.requestedProcessedDate = response.processDate;
        this.openRequestResultModal();
      }

      this.isRefreshing = true;
      if (this.allUsersList.length === 1 && this.p > 1) {
        this.p = this.p - 1;
      }
      this.loadRegistrationData(this.p);
    });
  }

  onPageClick(pg: number | '...') {
    if (pg === '...') return;
    this.onPageChange(pg);
  }

  onPageChange(page: number) {
    if (page < 1 || page > this.totalPages || page === this.p) return;
    this.loadRegistrationData(page);
  }

  goPrev() {
    if (this.isFirst) return;
    this.onPageChange(this.p - 1);
  }

  goNext() {
    if (this.isLast) return;
    this.onPageChange(this.p + 1);
  }

  trackUser(i: number, u: any) { return u.userId; }

  trackAccount(i: number, row: any) {
    return row.acc.accountNumber + '_' + row.user.userId;
  }

  private buildPages(): void {
    const total = this.totalPages;
    const currPage = this.p;
    this.pages = [];
    if (!total || total <= 1) {
      this.pages = [];
      return;
    }
    this.pages.push(1);

    if (total === 1) return;

    const start = Math.max(2, currPage - 2);
    const end = Math.min(total - 1, currPage + 2);

     if (start > 2) this.pages.push('...');

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) this.pages.push(i);
    }

    if (end < total - 1) this.pages.push('...');

    // Always last
    this.pages.push(total);

    // Deduplicate (edge cases)
    const cleaned: (number | '...')[] = [];
    let prev: number | '...' | undefined;
    for (const v of this.pages) {
      if (v !== prev) cleaned.push(v);
      prev = v;
    }
    this.pages = cleaned;
  }

  private openRejectModal(row: any, acc: any) {
    const modalRef = this.modalService.open(ModalRejectUserComponent, { backdrop: 'static' });
    modalRef.componentInstance.modalType = 'warning';
    modalRef.componentInstance.collectReasonOnly = true;
    modalRef.componentInstance.maxReasonLength = 250;
    modalRef.componentInstance.user_data = {
      user: row.user,
      accountNumber: acc.accountNumber
    };

    modalRef.componentInstance.successAction.subscribe((reason: string) => {
      acc.rejectionReason = reason;
      this.submitDecision(row, acc);
    });

    modalRef.componentInstance.cancelAction.subscribe(() => {
      acc.isSubmitting = false;
    });
  }

  private openRequestResultModal() {
    const modalRef = this.modalService.open(ModalAccountDecisionResultComponent, { backdrop: 'static' });
    modalRef.componentInstance.accountNumber = this.requestedAccountNumber;
    modalRef.componentInstance.processedBy = this.requestedProcessedBy;
    modalRef.componentInstance.processedDecision = this.requestedProcessedDecision;
    modalRef.componentInstance.processedReason = this.requestedProcessedReason;
    modalRef.componentInstance.processedDate = this.requestedProcessedDate;

    modalRef.componentInstance.successAction.subscribe(() => {
      this.requestedProcessedReason = '';
      return;
    });
  }

  private openPendingModal(row: any, acc: any) {
    const modalRef = this.modalService.open(ModalPendingUserComponent, { backdrop: 'static' });
    modalRef.componentInstance.modalType = 'warning';
    modalRef.componentInstance.maxReasonLength = 250;

    modalRef.componentInstance.successAction.subscribe((notes: string) => {
      acc.pendingNote = notes;
      this.submitDecision(row, acc);
    });

    modalRef.componentInstance.cancelAction.subscribe(() => {
      acc.isSubmitting = false;
    });
  }
}
